import { PaymentSuccessInfo } from "@app/models/PaymentSuccessInfo";
import { PaymentUiState } from "@app/services/shared/PaymentUiState.enum";
import { StoreBase } from "@app/services/stores/shared/StoreBase";
import { AppDate } from "@app/util/app-date";
import { todo } from "@app/util/todo";
import { DateObject } from "@app/models/DateObject";
import { PaymentMethod } from "@app/models/PaymentMethod";
import { PaymentRequest } from "@app/models/PaymentRequest";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { Property } from "@app/models/Property";
import { FixedDayStartDate } from '@app/models/FixedDayStartDate';
import { Amenity } from "@app/models/Amenity";
import { PaymentRequestDetail } from "@app/models/PaymentRequestDetail";

type PaymentState = {
    uiState: PaymentUiState;
    scheduleName: string;
    numberOfPayments: number;
    scheduleFrequency: string;
    startDate: string;
    scheduleFixedDayStartDate: FixedDayStartDate;
    endDate: string;
    property: Property;
    amountToPay: number;
    paymentMethod: PaymentMethod;
    paymentRequest: PaymentRequest;
    paymentScheduleRequest: PaymentSchedule;
    convenienceFee: number;
    paymentSuccessInfo: PaymentSuccessInfo;
    paymentScheduleType: string;
    availableAmenities: Amenity[];
    cartItems: PaymentRequestDetail[];

    /** exclusive to paymentEdit */
    propertyId: number;
    correlationId: string;
}

const APPDATE_FORMAT = "yyyy/MM/dd"

export class PaymentStoreBase extends StoreBase<PaymentState> {
    constructor(
        prefix: string,
        storage: Storage,
    ) {
        super(prefix, sessionStorage)
    }

    private readonly EMPTY_DATE: DateObject = Object.freeze({
        formattedFullDate: undefined,
        month: undefined,
        day: undefined,
        year: undefined
    });

    // New
    getUiState(): PaymentUiState {
        return this.get("uiState");
    }

    setUiState(value: PaymentUiState): void {
        return this.set("uiState", value)
    }

    getStartDate(): AppDate {
        const value = this.get("startDate")
        return value ? AppDate.fromString(value, APPDATE_FORMAT) : undefined
    }

    setStartDate(startDate: AppDate): void {
        this.set("startDate", startDate && startDate.format(APPDATE_FORMAT))
    }

    getScheduleFixedDayStartDate(): FixedDayStartDate {
        return this.get("scheduleFixedDayStartDate")
    }

    setScheduleFixedDayStartDate(scheduleFixedDayStartDate: FixedDayStartDate): void {
        this.set("scheduleFixedDayStartDate", scheduleFixedDayStartDate)
    }

    getEndDate(): AppDate {
        const value = this.get("endDate")
        return value ? AppDate.fromString(value, APPDATE_FORMAT) : undefined
    }

    setEndDate(endDate: AppDate): void {
        this.set("endDate", endDate && endDate.format(APPDATE_FORMAT))
    }

    getScheduleName(): string {
        return this.get("scheduleName")
    }

    setScheduleName(value: string): void {
        return this.set("scheduleName", value)
    }

    getNumberOfPayments(): number {
        return this.get("numberOfPayments")
    }

    setNumberOfPayments(value: number): void {
        this.set("numberOfPayments", value)
    }

    getScheduleFrequency(): string {
        return this.get("scheduleFrequency", "Monthly")
    }

    setScheduleFrequency(value: string): void {
        this.set("scheduleFrequency", value)
    }

    getProperty(): Property {
        return this.get("property")
    }

    setProperty(value: Property): void {
        this.set("property", value)
        this.setAmountToPay(null)
    }

    setAmountToPay(value: number): void {
        this.set("amountToPay", value)
    }

    getAmountToPay(): number {
        return this.get("amountToPay")
    }

    getPaymentMethod(): PaymentMethod {
        return this.get("paymentMethod")
    }

    setPaymentMethod(value: PaymentMethod): void {
        this.set("paymentMethod", value)
    }

    getPaymentRequest(): PaymentRequest {
        return this.get("paymentRequest")
    }

    setPaymentRequest(value: PaymentRequest): void {
        this.set("paymentRequest", value)
        this.setAmountToPay(null)
    }

    getPaymentScheduleRequest(): PaymentSchedule {
        return this.get("paymentScheduleRequest")
    }

    setPaymentScheduleRequest(value: PaymentSchedule): void {
        this.set("paymentScheduleRequest", value)
    }

    getConvenienceFee(): number {
        return this.get("convenienceFee")
    }

    setConvenienceFee(value: number): void {
        this.set("convenienceFee", value)
    }

    getPaymentSuccessInfo(): PaymentSuccessInfo {
        return this.get("paymentSuccessInfo")
    }

    setPaymentSuccessInfo(value: PaymentSuccessInfo): void {
        this.set("paymentSuccessInfo", value)
    }

    getPropertyId(): number {
        return this.get("propertyId")
    }

    setPropertyId(value: number): void {
        this.set("propertyId", value)
    }

    getPaymentScheduleType(): string {
        return this.get("paymentScheduleType")
    }

    setPaymentScheduleType(value: string): void {
        this.set("paymentScheduleType", value)
    }

    getAvailableAmenities(): Amenity[] {
        return this.get("availableAmenities")
    }

    setAvailableAmenities(value: Amenity[]): void {
        this.set("availableAmenities", value)
    }

    getCartItems(): PaymentRequestDetail[] {
        return this.get("cartItems")
    }

    setCartItems(value: PaymentRequestDetail[]): void {
        this.set("cartItems", value)
    }

    setCorrelationId(value: string){
        this.set("correlationId", value)
    }

    getCorrelationId(){
        return this.get("correlationId")
    }

    clearCorrelationId(){
        return this.remove("correlationId")
    }

    clearAll(): void {
        this.remove("uiState")
        this.remove("startDate")
        this.remove("scheduleFixedDayStartDate")
        this.remove("endDate")

        this.remove("scheduleName")
        this.remove("numberOfPayments")
        this.remove("scheduleFrequency")
        this.remove("property")
        this.remove("amountToPay")
        this.remove("paymentMethod")
        this.remove("paymentScheduleType")

        todo("Ensure that not clearing paymentRequest is okay")
        // this.remove("paymentRequest")
        this.remove("paymentSuccessInfo")

        this.remove("paymentScheduleRequest")
        this.remove("convenienceFee")
        this.remove("propertyId")
        this.remove("availableAmenities")
        this.remove("cartItems")
        this.remove("correlationId")
    }
}
